import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useBusiness } from '@/contexts/BusinessContext';
import { useMonth } from '@/contexts/MonthContext';
import { supabase } from '@/lib/supabase';
import LogoSpinner from '@/components/LogoSpinner';
import {
  getMoneyFlowEvents,
  createMoneyFlowEvent,
  updateMoneyFlowEvent,
  deleteMoneyFlowEvent,
  subscribeToMoneyFlowEvents,
  getBankRate,
} from '@/lib/moneyFlowApi';
import { getMonthRange, getMonthName } from '@/lib/dateUtils';
import type { MoneyFlowEvent } from '@/types/database';
import {
  ArrowRightLeft,
  Plus,
  RefreshCw,
  Search,
  ChevronDown,
  Calendar,
  X,
} from 'lucide-react';
import InfoTooltip from '@/components/InfoTooltip';
import AddMoneyFlowModal from './moneyflow/AddMoneyFlowModal';
import EventDetailModal from './moneyflow/EventDetailModal';

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

export default function MoneyFlowPage() {
  const { selectedBusiness } = useBusiness();
  const { selectedMonth: globalMonth, selectMonth } = useMonth();
  const selectedYear = globalMonth.year;
  const selectedMonthNum = globalMonth.month;
  const monthYear = `${selectedYear}-${String(selectedMonthNum).padStart(2, '0')}`;
  const monthYearRef = useRef(monthYear);
  monthYearRef.current = monthYear;

  const [events, setEvents] = useState<MoneyFlowEvent[]>([]);
  const [bankRate, setBankRate] = useState(15.42);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<MoneyFlowEvent | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MoneyFlowEvent | null>(null);
  const [viewingEvent, setViewingEvent] = useState<MoneyFlowEvent | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async (my: string) => {
    const [y, m] = my.split('-').map(Number);
    const { start, end } = getMonthRange(y, m);
    const [evts, rate] = await Promise.all([
      getMoneyFlowEvents(start.split('T')[0], end.split('T')[0]),
      getBankRate(),
    ]);
    if (monthYearRef.current !== my) return;
    setEvents(evts);
    setBankRate(rate);
    setLoading(false);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData(monthYear).catch(() => {
      if (monthYearRef.current === monthYear) setLoading(false);
    });
  }, [monthYear, fetchData]);

  useEffect(() => {
    const reload = () => fetchData(monthYearRef.current).catch(() => {});
    const ch = subscribeToMoneyFlowEvents(reload);
    return () => { supabase.removeChannel(ch); };
  }, [fetchData]);

  function refresh() {
    setRefreshing(true);
    fetchData(monthYearRef.current).catch(() => {}).finally(() => setRefreshing(false));
  }

  async function handleSave(data: {
    event_date: string;
    wise_usd_sent: number;
    fee_usd: number;
    exchange_rate_lkr_usd: number;
    p2p_rate_lkr_usdt: number;
    usdt_sent: number;
    mvr_rate_per_usdt: number;
    notes: string;
  }) {
    if (!selectedBusiness) return;
    if (editingEvent) {
      await updateMoneyFlowEvent(editingEvent.id, data);
    } else {
      await createMoneyFlowEvent({
        ...data,
        business_id: selectedBusiness.id,
        business_name: selectedBusiness.name,
      });
    }
    setModalVisible(false);
    setEditingEvent(null);
    refresh();
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await deleteMoneyFlowEvent(confirmDelete.id);
      setConfirmDelete(null);
      refresh();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  }

  function openEdit(ev: MoneyFlowEvent) {
    setEditingEvent(ev);
    setModalVisible(true);
  }

  function openAdd() {
    setEditingEvent(null);
    setModalVisible(true);
  }

  const calcLkr = (e: MoneyFlowEvent) => (e.wise_usd_sent - e.fee_usd) * e.exchange_rate_lkr_usd;
  const calcUsdtBought = (e: MoneyFlowEvent) => {
    const lkr = calcLkr(e);
    return e.p2p_rate_lkr_usdt > 0 ? lkr / e.p2p_rate_lkr_usdt : 0;
  };
  const calcMvrOut = (e: MoneyFlowEvent) => e.usdt_sent * e.mvr_rate_per_usdt;
  const calcProfit = (e: MoneyFlowEvent) => calcMvrOut(e) - (e.wise_usd_sent * bankRate);

  const filtered = useMemo(() => {
    if (!debouncedSearch.trim()) return events;
    const q = debouncedSearch.toLowerCase();
    return events.filter(e =>
      e.business_name.toLowerCase().includes(q) ||
      (e.notes || '').toLowerCase().includes(q) ||
      e.wise_usd_sent.toString().includes(q)
    );
  }, [events, debouncedSearch]);

  const totals = useMemo(() => {
    const t = {
      wiseUsd: 0, feeUsd: 0, lkrIn: 0, usdtBought: 0,
      usdtSent: 0, mvrOut: 0, profit: 0,
    };
    for (const e of events) {
      t.wiseUsd += e.wise_usd_sent;
      t.feeUsd += e.fee_usd;
      t.lkrIn += calcLkr(e);
      t.usdtBought += calcUsdtBought(e);
      t.usdtSent += e.usdt_sent;
      t.mvrOut += calcMvrOut(e);
      t.profit += calcProfit(e);
    }
    return t;
  }, [events, bankRate]);

  const summaryCards = [
    {
      label: 'Total USD Sent',
      value: `$${totals.wiseUsd.toFixed(2)}`,
      tooltip: { title: 'Total Wise USD', description: 'Total USD sent via Wise across all companies this month.' },
    },
    {
      label: 'Total LKR In',
      value: `LKR ${totals.lkrIn.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      tooltip: { title: 'LKR Received', description: 'Total LKR received after fees and exchange conversion.' },
    },
    {
      label: 'Total USDT Bought',
      value: `${totals.usdtBought.toFixed(2)} USDT`,
      tooltip: { title: 'USDT Purchased', description: 'Total USDT bought via P2P from LKR.' },
    },
    {
      label: 'Total MVR In',
      value: `MVR ${totals.mvrOut.toFixed(2)}`,
      tooltip: { title: 'MVR Revenue', description: 'Total MVR received from selling USDT.' },
    },
    {
      label: 'Net Profit',
      value: `MVR ${totals.profit.toFixed(2)}`,
      tooltip: { title: 'Net Profit', description: `Profit = MVR In - (Wise USD x bank rate of ${bankRate}). Positive means you earned more MVR than bank cost.` },
      color: totals.profit >= 0 ? 'text-emerald-400' : 'text-red-700',
    },
  ];

  const now = new Date();
  const currentYear = now.getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear + 1];

  const fmt = (n: number, d = 2) => n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <LogoSpinner size={48} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="glass-header shrink-0 px-4 sm:px-6 py-4">
        {searchOpen && (
          <div className="absolute inset-0 z-10 flex items-center px-4 sm:px-6" style={{ background: 'rgba(10, 25, 20, 0.98)' }}>
            <div className="flex-1 flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-3 py-2.5">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by company, notes..."
                className="flex-1 text-sm text-white bg-transparent outline-none placeholder:text-slate-400"
                autoFocus
              />
              <button
                onClick={() => { setSearch(''); setSearchOpen(false); }}
                className="text-slate-400 hover:text-white transition-colors p-1"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-white">Money Flow</h1>
            <p className="text-xs text-white mt-0.5 hidden sm:block">
              {events.length} event{events.length !== 1 ? 's' : ''} &middot; {getMonthName(selectedMonthNum)} {selectedYear}
            </p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button rounded-xl text-white hover:text-teal-400 text-xs font-medium"
              title="Search"
            >
              <Search size={14} />
              <span className="hidden sm:inline">Search</span>
            </button>
            <button
              onClick={() => setMonthDropdownOpen(true)}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button text-white rounded-xl text-xs font-medium"
              title={`${getMonthName(selectedMonthNum)} ${selectedYear}`}
            >
              <Calendar size={14} className="sm:hidden text-teal-400" />
              <span className="hidden sm:inline">{getMonthName(selectedMonthNum)} {selectedYear}</span>
              <ChevronDown size={13} className="hidden sm:inline" />
            </button>
            <button
              onClick={refresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-2 glass-button text-white rounded-xl hover:text-teal-400 disabled:opacity-50 text-xs font-medium"
              title="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={openAdd}
              className="flex items-center gap-1.5 p-2 sm:px-4 sm:py-2 glass-button-active text-white rounded-xl text-xs font-medium"
              title="Add Event"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Add Event</span>
            </button>
          </div>
        </div>
        <p className="text-xs text-white mt-2 sm:hidden">
          {events.length} event{events.length !== 1 ? 's' : ''} &middot; {getMonthName(selectedMonthNum)} {selectedYear}
        </p>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {events.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
            {summaryCards.map(card => (
              <InfoTooltip key={card.label} title={card.tooltip.title} description={card.tooltip.description} position="bottom">
                <div className="glass-panel rounded-xl px-3 py-2 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 cursor-default h-full">
                  <p className="text-[10px] font-medium text-white mb-0.5">{card.label}</p>
                  <p className={`text-sm font-bold ${card.color || 'text-white'} whitespace-nowrap`}>{card.value}</p>
                </div>
              </InfoTooltip>
            ))}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="bg-white/10 rounded-2xl p-5 mb-4">
              <ArrowRightLeft size={32} className="text-white" />
            </div>
            <h3 className="text-sm font-semibold text-white mb-1">
              {search ? 'No events found' : 'No money flow events yet'}
            </h3>
            <p className="text-xs text-white mb-5">
              {search ? 'Try adjusting your search' : 'Track USD to USDT to MVR conversions'}
            </p>
            {!search && (
              <button
                onClick={openAdd}
                className="flex items-center gap-1.5 px-4 py-2 glass-button-active text-white rounded-xl text-xs font-medium"
              >
                <Plus size={14} />
                Add First Event
              </button>
            )}
          </div>
        ) : (
          <div className="glass-panel rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/20">
                <thead className="bg-white/20">
                  <tr>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-white uppercase tracking-wider">Date</th>
                    <th className="px-3 py-3 text-left text-[10px] font-medium text-white uppercase tracking-wider">Company</th>
                    <th className="px-3 py-3 text-right text-[10px] font-medium text-white uppercase tracking-wider">Wise USD</th>
                    <th className="px-3 py-3 text-right text-[10px] font-medium text-white uppercase tracking-wider">Fee</th>
                    <th className="px-3 py-3 text-right text-[10px] font-medium text-white uppercase tracking-wider">LKR In</th>
                    <th className="px-3 py-3 text-right text-[10px] font-medium text-white uppercase tracking-wider">USDT Bought</th>
                    <th className="px-3 py-3 text-right text-[10px] font-medium text-white uppercase tracking-wider">USDT Sent</th>
                    <th className="px-3 py-3 text-right text-[10px] font-medium text-white uppercase tracking-wider">MVR In</th>
                    <th className="px-3 py-3 text-right text-[10px] font-medium text-white uppercase tracking-wider">Profit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/20">
                  {filtered.map(ev => {
                    const lkrIn = calcLkr(ev);
                    const usdtBought = calcUsdtBought(ev);
                    const mvrOut = calcMvrOut(ev);
                    const profit = calcProfit(ev);
                    return (
                      <tr key={ev.id} className="glass-hover cursor-pointer" onClick={() => setViewingEvent(ev)}>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-white">{ev.event_date}</td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/10 text-white ring-1 ring-inset ring-white/10">
                            {ev.business_name}
                          </span>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-white text-right font-medium">${fmt(ev.wise_usd_sent)}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-white text-right">${fmt(ev.fee_usd)}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-teal-400 text-right font-medium">
                          {lkrIn > 0 ? `LKR ${fmt(lkrIn, 0)}` : '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-sky-400 text-right font-medium">
                          {usdtBought > 0 ? fmt(usdtBought) : '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-white text-right font-medium">
                          {ev.usdt_sent > 0 ? fmt(ev.usdt_sent) : '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-teal-400 text-right font-semibold">
                          {mvrOut > 0 ? `MVR ${fmt(mvrOut)}` : '-'}
                        </td>
                        <td className={`px-3 py-3 whitespace-nowrap text-xs text-right font-bold ${profit >= 0 ? 'text-emerald-400' : 'text-red-600'}`}>
                          {mvrOut > 0 ? `MVR ${fmt(profit)}` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="glass-inner border-t-2 border-white/10/60">
                  <tr>
                    <td className="px-3 py-3 text-xs font-bold text-white" colSpan={2}>Totals</td>
                    <td className="px-3 py-3 text-xs font-bold text-white text-right">${fmt(totals.wiseUsd)}</td>
                    <td className="px-3 py-3 text-xs font-bold text-white text-right">${fmt(totals.feeUsd)}</td>
                    <td className="px-3 py-3 text-xs font-bold text-teal-400 text-right">LKR {fmt(totals.lkrIn, 0)}</td>
                    <td className="px-3 py-3 text-xs font-bold text-sky-400 text-right">{fmt(totals.usdtBought)}</td>
                    <td className="px-3 py-3 text-xs font-bold text-white text-right">{fmt(totals.usdtSent)}</td>
                    <td className="px-3 py-3 text-xs font-bold text-teal-400 text-right">MVR {fmt(totals.mvrOut)}</td>
                    <td className={`px-3 py-3 text-xs font-bold text-right ${totals.profit >= 0 ? 'text-emerald-400' : 'text-red-600'}`}>
                      MVR {fmt(totals.profit)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {events.length > 0 && (
          <div className="mt-3 text-right">
            <p className="text-[10px] text-white">
              Profit = MVR In - (Wise USD x bank rate {bankRate.toFixed(2)})
            </p>
          </div>
        )}
      </div>

      {modalVisible && (
        <AddMoneyFlowModal
          onClose={() => { setModalVisible(false); setEditingEvent(null); }}
          onSave={handleSave}
          editing={editingEvent}
        />
      )}

      {viewingEvent && (
        <EventDetailModal
          event={viewingEvent}
          bankRate={bankRate}
          canEdit={selectedBusiness?.id === viewingEvent.business_id}
          onClose={() => setViewingEvent(null)}
          onEdit={() => {
            const ev = viewingEvent;
            setViewingEvent(null);
            openEdit(ev);
          }}
          onDelete={() => {
            const ev = viewingEvent;
            setViewingEvent(null);
            setConfirmDelete(ev);
          }}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="glass-modal rounded-2xl max-w-sm w-full p-5">
            <h2 className="text-base font-bold text-white mb-1">Delete Event</h2>
            <p className="text-xs text-white mb-4">Are you sure? This action cannot be undone.</p>
            <div className="glass-inner rounded-xl p-3.5 mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-white">Date:</span>
                <span className="text-sm font-bold text-white">{confirmDelete.event_date}</span>
              </div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-white">USD Sent:</span>
                <span className="text-sm font-bold text-white">${confirmDelete.wise_usd_sent.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-white">Company:</span>
                <span className="text-xs font-medium text-white">{confirmDelete.business_name}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2.5 text-sm glass-button text-white rounded-xl font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2.5 text-sm bg-red-600 text-white rounded-xl glass-hover-red font-medium shadow-sm shadow-red-600/20"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {monthDropdownOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="glass-modal rounded-2xl w-full max-w-sm max-h-[50vh] flex flex-col">
            <div className="flex justify-between items-center p-5 border-b border-white/10">
              <h2 className="text-base font-bold text-white">Select Month</h2>
              <button onClick={() => setMonthDropdownOpen(false)} className="p-1.5 rounded-lg glass-hover">
                <X size={18} className="text-white" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {yearOptions.map(y => (
                <div key={y}>
                  <p className={`text-[11px] font-bold uppercase tracking-wider px-2 pt-3 pb-1 ${y === selectedYear ? 'text-teal-400' : 'text-white/50'}`}>{y}</p>
                  {MONTHS.map(m => {
                    const my = `${y}-${String(m).padStart(2, '0')}`;
                    const isActive = my === monthYear;
                    return (
                      <button
                        key={my}
                        onClick={() => { selectMonth({ year: y, month: m }); setMonthDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 rounded-xl text-sm mb-0.5 transition-all duration-150 ${
                          isActive ? 'bg-teal-500/10 text-teal-400 font-semibold' : 'text-white glass-hover'
                        }`}
                      >
                        {getMonthName(m)}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
